/*
 * Copyright (c) 2026 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, July 31st 2026, 3:34:25 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import { FileText as FileTextIcon } from "react-feather";
import { UserTypeMultiCheckBoxSelector } from "common/components/UserTypeSelector";
import { AddNewFormButtonsCTA } from "form-builder/components/AddNewFormButton";
import { EmptyFormsCTA } from "form-builder/components/EmptyFormsCTA";
import { ProcessDefinitionSubmissionFormLinkage, USER_TYPES } from "lib/schemas";
import { useOnAPICreateSubmissionForm } from "service-builder/hooks";
import { useServiceBuilderContext } from "service-builder/providers";
import { useProcessDefinitionsAPI } from "service-builder/services";
import { COLORSCHEME } from "theme/styles";
import { APIListProgressWrapper } from "ui-widgets";

export const ProcessFormLinkageSettings = () => {
  const [linkages, setLinkages] = React.useState([]);
  const [loadLinkages, setLoadLinkages] = React.useState(true);
  const {
    processDefinitionContext: { currentProcessDefinition },
  } = useServiceBuilderContext();
  const { useProcessFormLinkageAPI } = useProcessDefinitionsAPI();
  const { isMakingAPIRequest, isAPIError, httpResponseStatusCode, fetchLinkages, patchLinkage } =
    useProcessFormLinkageAPI();

  const { onSaveFormInfo, defaultNewFormConfiguration } = useOnAPICreateSubmissionForm();

  React.useEffect(() => {
    if (!loadLinkages) return;
    fetchLinkages(currentProcessDefinition.id, setLinkages);
    setLoadLinkages(false);
  }, [currentProcessDefinition.id, fetchLinkages, loadLinkages]);

  const updateLinkage = (newLinkage: ProcessDefinitionSubmissionFormLinkage) =>
    setLinkages((prev) =>
      prev.map((link) => {
        if (link.id === newLinkage.id) return newLinkage;
        return link;
      })
    );

  const onSelectUserType: ProcessFormLinkageItemProps["onSelectUserType"] = (linkageId, userType) => {
    const linkage = linkages.find((link) => link.id === linkageId);
    if (!linkage) return;

    const newLinkage: ProcessDefinitionSubmissionFormLinkage = { ...linkage };

    if (newLinkage.allowed_initiator_types.includes(userType))
      newLinkage.allowed_initiator_types = newLinkage.allowed_initiator_types.filter((t) => t !== userType);
    else newLinkage.allowed_initiator_types = [...newLinkage.allowed_initiator_types, userType];

    updateLinkage(newLinkage);

    patchLinkage(
      currentProcessDefinition.id,
      linkageId,
      { allowed_initiator_types: newLinkage.allowed_initiator_types },
      updateLinkage
    );
  };

  return (
    <APIListProgressWrapper
      showLoader={isMakingAPIRequest && linkages.length === 0}
      isAPIError={isAPIError}
      items={linkages}
      httpResponseStatusCode={httpResponseStatusCode}
      noDataMessage={
        <EmptyFormsCTA
          noDataMessage={`You have no submission forms linked to ${currentProcessDefinition.title}`}
          ctaButtons={
            <AddNewFormButtonsCTA
              defaultNewFormConfiguration={defaultNewFormConfiguration}
              form_type="application_form"
              allowedFormTypes={["application_form"]}
              popUpTitle="Add Submission form"
              size="large"
              onAPISaveFormInfoCallBackFn={(form) => {
                onSaveFormInfo(form, () => setLoadLinkages(true));
              }}
            />
          }
        />
      }
    >
      <List sx={{ bgcolor: "background.paper" }}>
        {linkages.map((linkage) => (
          <ProcessFormLinkageItem linkage={linkage} onSelectUserType={onSelectUserType} />
        ))}
      </List>
    </APIListProgressWrapper>
  );
};

type ProcessFormLinkageItemProps = {
  linkage: ProcessDefinitionSubmissionFormLinkage;
  onSelectUserType: (
    linkageId: ProcessDefinitionSubmissionFormLinkage["id"],
    userType: keyof typeof USER_TYPES
  ) => void;
};

const ProcessFormLinkageItem = ({ linkage, onSelectUserType }: ProcessFormLinkageItemProps) => {
  const {
    processDefinitionContext: { currentProcessDefinition },
  } = useServiceBuilderContext();
  return (
    <Stack key={linkage.id}>
      <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} sx={{ padding: 2 }}>
        <Stack direction="row" alignItems="center" sx={{ width: 280, flexShrink: 0 }}>
          <ListItemIcon>
            <FileTextIcon color={COLORSCHEME.secondary} />
          </ListItemIcon>
          <ListItemText primary={linkage.form_detail.title} />
        </Stack>
        <UserTypeMultiCheckBoxSelector
          options={["CLIENT", "WORKER"]}
          selectedUserTypes={linkage.allowed_initiator_types as any}
          onSelectUserType={(userType) => onSelectUserType(linkage.id, userType)}
          userTypesDisplay={{
            CLIENT: {
              label: "Clients",
              description: `Clients will be able to submit ${linkage.form_detail.title} in order to initiate '${currentProcessDefinition.title}' processes`,
            },
            WORKER: {
              label: "Workers",
              description: `Workers will be able to submit ${linkage.form_detail.title} in order to initiate '${currentProcessDefinition.title}' processes`,
            },
          }}
        />
      </Stack>
      <Divider />
    </Stack>
  );
};
